import { CommentActionType } from '@confluence/comments-data';
import type { CommentData, CommentsDataMap, DataMap, ReplyData } from '@confluence/comments-data';
import { fg } from '@confluence/feature-gating';
import type {
	CommentLocation,
	CommentInlineLocation,
	ActiveCommentInlineResolveProperties,
} from '@confluence/comments-panel-queries';

import { getCommentsSortedOldestToNewest } from './commentsPanelHelper';

type CommentWithRemovalStatus = Pick<CommentData | ReplyData, 'wasRemovedByAnotherUser'>;
type IsCommentValid = (comment: CommentData | ReplyData) => boolean;
type VisibleInlineCommentsByDanglingStatus = [CommentData[], CommentData[]];

type GetVisibleCommentThreadsOptions = {
	isCommentValid?: IsCommentValid;
	commentsDataMap: CommentsDataMap;
	includeInlineComments: boolean;
	includeGeneralComments: boolean;
	orderedAnnotationIds: string[];
};

const isParentCommentOpen = (commentData: CommentData): boolean => commentData.isOpen;
const isUnreadComment = (commentData: CommentData | ReplyData): boolean => commentData.isUnread;
const hasUnreadReplies = (commentData: CommentData): boolean =>
	commentData.replies?.some((reply) => reply.isUnread) ?? false;

const wasCommentRemovedByAnotherUser = (comment: CommentWithRemovalStatus): boolean =>
	Boolean(comment.wasRemovedByAnotherUser);

const wasCommentDeletedByAnotherUser = (comment: CommentWithRemovalStatus): boolean =>
	comment.wasRemovedByAnotherUser === CommentActionType.DELETE_COMMENT;

const isInlineLocation = (location: CommentLocation): location is CommentInlineLocation =>
	'inlineMarkerRef' in location;

const isParentComment = (comment: CommentData | ReplyData): comment is CommentData =>
	'isOpen' in comment;

const getVisibleGeneralComments = (
	isCommentValid: IsCommentValid,
	comments: DataMap,
): CommentData[] =>
	Object.values(comments).reduce<CommentData[]>((acc, comment) => {
		if (isCommentValid(comment)) {
			acc.push({
				...comment,
				replies: comment.replies.filter(isCommentValid),
			});
		}
		return acc;
	}, []);

const getNextInlineResolveProperties = (
	inlineResolveProperties: ActiveCommentInlineResolveProperties | null,
	isDangling: boolean,
): { inlineResolveProperties?: ActiveCommentInlineResolveProperties } => {
	if (!inlineResolveProperties) return {};

	return {
		inlineResolveProperties: {
			...inlineResolveProperties,
			isDangling,
		},
	};
};

const updateCommentLocation = (
	comment: CommentData,
	isDangling: boolean,
): CommentData['location'] => {
	const commentLocation = comment.location;
	if (!isInlineLocation(commentLocation)) return commentLocation;

	return {
		...commentLocation,
		...getNextInlineResolveProperties(commentLocation.inlineResolveProperties, isDangling),
	};
};

const getNextComment = (comment: CommentData, isDangling: boolean): CommentData => ({
	...comment,
	location: updateCommentLocation(comment, isDangling),
});

const getVisibleInlineComments = (
	isCommentValid: IsCommentValid,
	comments: DataMap,
	orderedAnnotationIds: string[],
): VisibleInlineCommentsByDanglingStatus => {
	const activeMarkerRefs = new Set(orderedAnnotationIds);
	const nonDangling: CommentData[] = [];
	const dangling: CommentData[] = [];

	const validComments = Object.values(comments).reduce<CommentData[]>((acc, comment) => {
		if (isCommentValid(comment)) {
			acc.push({
				...comment,
				replies: comment.replies.filter(isCommentValid),
			});
		}
		return acc;
	}, []);

	for (const comment of validComments) {
		const commentLocation = comment.location;
		const inlineMarkerRef = isInlineLocation(commentLocation)
			? commentLocation.inlineMarkerRef
			: null;

		const isDangling = !inlineMarkerRef || !activeMarkerRefs.has(inlineMarkerRef);
		const updatedComment = getNextComment(comment, isDangling);

		if (isDangling) {
			dangling.push(updatedComment);
		} else {
			nonDangling.push(updatedComment);
		}
	}

	return [nonDangling, dangling];
};

export const isCommentResolved = (comment: CommentData | ReplyData): boolean => {
	if (wasCommentDeletedByAnotherUser(comment)) return false;

	return isParentComment(comment) ? !isParentCommentOpen(comment) : true;
};

export const isCommentOpen = (comment: CommentData | ReplyData): boolean => {
	if (wasCommentRemovedByAnotherUser(comment)) return false;

	return isParentComment(comment) ? isParentCommentOpen(comment) : true;
};

export const isCommentUnread = (comment: CommentData | ReplyData): boolean => {
	if (wasCommentRemovedByAnotherUser(comment)) return false;

	return isParentComment(comment)
		? hasUnreadReplies(comment) || isUnreadComment(comment)
		: isUnreadComment(comment);
};

const sortInlineCommentsByOrderedActiveAnnotationIdList = (
	comments: CommentData[],
	orderedAnnotationIds: string[],
): CommentData[] => {
	const commentsByMarkerRef = new Map(
		comments.map((comment) => {
			const commentLocation = comment.location;
			const inlineMarkerRef = isInlineLocation(commentLocation)
				? commentLocation.inlineMarkerRef
				: null;

			return [inlineMarkerRef, comment];
		}),
	);

	return orderedAnnotationIds
		.map((id) => commentsByMarkerRef.get(id))
		.filter((comment): comment is CommentData => comment !== undefined);
};

const getVisibleInlineThreads = (
	isCommentValid: IsCommentValid,
	commentsDataMap: CommentsDataMap,
	orderedAnnotationIds: string[],
): CommentData[] => {
	const [nonDanglingComments, danglingComments] = getVisibleInlineComments(
		isCommentValid,
		commentsDataMap.inline,
		orderedAnnotationIds,
	);

	const sortedNonDanglingComments = sortInlineCommentsByOrderedActiveAnnotationIdList(
		nonDanglingComments,
		orderedAnnotationIds,
	);

	return fg('confluence_frontend_dangling_comments')
		? [...sortedNonDanglingComments, ...danglingComments]
		: sortedNonDanglingComments;
};

export const getVisibleCommentThreads = ({
	isCommentValid = isCommentOpen,
	commentsDataMap,
	orderedAnnotationIds,
	includeInlineComments,
	includeGeneralComments,
}: GetVisibleCommentThreadsOptions): CommentData[] => {
	const threads: CommentData[] = [];

	if (includeInlineComments) {
		threads.push(...getVisibleInlineThreads(isCommentValid, commentsDataMap, orderedAnnotationIds));
	}

	if (includeGeneralComments) {
		threads.push(
			...getVisibleGeneralComments(isCommentValid, commentsDataMap.general).sort(
				getCommentsSortedOldestToNewest,
			),
		);
	}

	return threads;
};
